/**
 * Milestone Celebration Component
 * 
 * Shows a beautiful celebration modal when a member reaches
 * a focus journey milestone (Day 30, 60, or 90).
 * Includes confetti, badge award, and encouraging message.
 */

import { useState, useEffect } from 'react';
import { useCelebration } from '@/context/CelebrationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Trophy, Sparkles, Star, Crown, Flame, Heart, X
} from 'lucide-react';

// Milestone configurations
const MILESTONE_CONFIG = {
  30: {
    name: 'Foundation',
    icon: '🏗️',
    color: 'from-emerald-400 to-teal-600',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    celebration: 'confetti'
  },
  60: {
    name: 'Momentum',
    icon: '🚀',
    color: 'from-violet-400 to-purple-600',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    celebration: 'hearts'
  },
  90: {
    name: 'Transformation',
    icon: '👸',
    color: 'from-amber-400 to-orange-600',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    celebration: 'epicCelebrate'
  }
};

const MilestoneCelebration = ({ milestone, onClose }) => {
  const { celebrate, grandCelebrate, epicCelebrate } = useCelebration();
  const [isVisible, setIsVisible] = useState(false);
  const [badgeRevealed, setBadgeRevealed] = useState(false);

  const config = MILESTONE_CONFIG[milestone?.day] || MILESTONE_CONFIG[30];

  useEffect(() => {
    if (milestone) {
      // Animate in
      setIsVisible(true);
      
      // Trigger celebration after a short delay
      const celebrationDelay = setTimeout(() => {
        if (config.celebration === 'epicCelebrate') {
          epicCelebrate();
        } else if (config.celebration === 'hearts') {
          celebrate('hearts');
        } else {
          celebrate('confetti');
        }
        
        // Reveal badge with delay
        setTimeout(() => setBadgeRevealed(true), 500);
      }, 300);
      
      return () => clearTimeout(celebrationDelay);
    }
  }, [milestone, config.celebration, celebrate, epicCelebrate]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  if (!milestone) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative w-full max-w-md transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        data-testid="milestone-celebration-modal"
      >
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute -top-3 -right-3 z-10 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
        
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${config.color} p-8 text-center`}>
            <div className="relative inline-block">
              <span className="text-6xl animate-bounce block">{config.icon}</span>
              <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-white animate-pulse" />
            </div>
            <h2 className="text-white text-3xl font-heading font-bold mt-4">
              Day {milestone.day}!
            </h2>
            <p className="text-white/90 text-lg mt-1">
              {config.name} Complete
            </p>
          </div>
          
          {/* Content */}
          <div className="p-8 text-center">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {milestone.message}
            </p>
            
            {/* Badge Award */}
            {milestone.badge && (
              <div 
                className={`transform transition-all duration-500 ${
                  badgeRevealed ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                }`}
              >
                <p className="text-sm text-gray-500 mb-3">You earned a badge!</p>
                <div className={`inline-flex items-center gap-3 px-6 py-4 rounded-2xl ${config.bgColor} border-2 border-dashed ${config.textColor.replace('text', 'border')}`}>
                  <span className="text-4xl">{milestone.badge.icon}</span>
                  <div className="text-left">
                    <p className={`font-bold ${config.textColor}`}>
                      {milestone.badge.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {milestone.badge.description}
                    </p>
                    <Badge className="mt-1 bg-yellow-100 text-yellow-700 text-xs">
                      +{milestone.badge.points} diamonds
                    </Badge>
                  </div>
                </div>
              </div>
            )}
            
            {/* Encouragement based on milestone */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 italic">
                {milestone.day === 30 && '"Your foundation determines your elevation. Well done, Sis."'}
                {milestone.day === 60 && '"Momentum is a force multiplier. Keep going, Queen."'}
                {milestone.day === 90 && '"You did it! You are the transformation you were seeking."'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="mt-6 flex gap-3 justify-center">
              <Button 
                onClick={handleClose}
                className={`bg-gradient-to-r ${config.color} hover:opacity-90 text-white px-8`}
                data-testid="milestone-continue-btn"
              >
                {milestone.day === 90 ? 'Begin New Journey' : 'Keep Going'}
              </Button>
            </div>
            
            {/* Progress indicator */}
            <div className="mt-6 flex items-center justify-center gap-2">
              <div className={`h-3 w-3 rounded-full ${milestone.day >= 30 ? 'bg-emerald-500' : 'bg-gray-200'}`} />
              <div className={`h-1 w-8 ${milestone.day >= 30 ? 'bg-emerald-500' : 'bg-gray-200'}`} />
              <div className={`h-3 w-3 rounded-full ${milestone.day >= 60 ? 'bg-violet-500' : 'bg-gray-200'}`} />
              <div className={`h-1 w-8 ${milestone.day >= 60 ? 'bg-violet-500' : 'bg-gray-200'}`} />
              <div className={`h-3 w-3 rounded-full ${milestone.day >= 90 ? 'bg-amber-500' : 'bg-gray-200'}`} />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Foundation → Momentum → Transformation
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilestoneCelebration;
